import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kioskApi, setKioskDeviceToken, saveKioskCtx, formatKioskError } from "@/lib/kioskApi";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTheme } from "@/components/ThemeProvider";
import { Loader2, TabletSmartphone, Lock } from "lucide-react";

/**
 * Public kiosk pairing page. Enters the one-time 8-char code from admin,
 * receives a long-lived (IP-bound) device JWT, redirects to /kiosk.
 */
export default function KioskPairPage() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";

  // If already paired, jump straight to /kiosk
  useEffect(() => {
    const tok = localStorage.getItem("clockin.kiosk_device");
    if (tok) nav("/kiosk", { replace: true });
  }, [nav]);

  const clean = (raw) => raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (clean(code).length !== 8) {
      setErr("Enter the full 8-character code.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await kioskApi.post("/kiosk/pair", { pairing_code: code });
      setKioskDeviceToken(data.device_jwt);
      saveKioskCtx({
        tenant: data.tenant,
        worksite: data.worksite,
        device: data.device,
        config: data.config,
        expires_at: data.expires_at,
      });
      nav("/kiosk", { replace: true });
    } catch (e) {
      setErr(formatKioskError(e) || "Pairing failed");
    } finally {
      setBusy(false);
    }
  };

  const pretty = (() => {
    const c = clean(code);
    return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
  })();

  return (
    <div className="brand-navy-gradient min-h-screen text-white" data-testid="kiosk-pair-page">
      <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6 py-10">
        <div className="mb-10 flex flex-col items-center gap-3">
          <Logo variant="dark" />
          <div className="mt-2 rounded-full bg-white/10 p-4 text-white">
            <TabletSmartphone className="h-8 w-8" />
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Pair this tablet</h1>
          <p className="max-w-sm text-center text-sm text-white/70">
            Ask an admin to open <span className="font-mono text-white">Kiosks</span> in {brand}
            and create (or regenerate) a pairing code for this worksite.
          </p>
        </div>

        <form onSubmit={submit} className="w-full space-y-6 rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur">
          <div className="space-y-3">
            <label htmlFor="code" className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
              Pairing code
            </label>
            <Input
              id="code"
              autoFocus
              inputMode="text"
              autoComplete="off"
              value={pretty}
              onChange={(e) => setCode(clean(e.target.value))}
              placeholder="ABCD-1234"
              className="h-16 rounded-xl border-white/15 bg-white/5 text-center font-mono text-2xl tracking-[0.35em] text-white placeholder:text-white/25 focus-visible:ring-secondary"
              data-testid="kiosk-pair-code-input"
            />
            <p className="text-xs text-white/50">
              Case-insensitive. Dashes optional. Codes expire 10 minutes after creation.
            </p>
          </div>

          {err && (
            <Alert variant="destructive" className="border-red-400/40 bg-red-500/10 text-red-100" data-testid="kiosk-pair-error">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={busy || clean(code).length !== 8}
            className="h-14 w-full gap-2 rounded-xl bg-secondary text-base text-white hover:bg-secondary/90"
            data-testid="kiosk-pair-submit"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />} Pair device
          </Button>
        </form>

        <div className="mt-8 text-center text-xs text-white/40">
          Once paired, this tablet stays authorized for 90 days.
        </div>
      </div>
    </div>
  );
}
